import { Injectable } from '@angular/core';

import { combineLatest, filter, map } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  GraphReponsePaged,
  HandleComplexRequest,
  graphAggregatorEndpoint,
  graphQlUpdateFields,
} from '@camelot/server';
import { missionProps, projectAddressProps, projectProps } from '@camelot/services';
import { contactProps } from '@camelot/services';
import { userProps } from '@camelot/user';
import { DateLike, isNonNullable, toServerDate } from '@camelot/utils';

import { CalendarEntry } from './dto/calendar-entry';
import { VisitCreationPayloadInput } from './dto/post/visit-creation-payload-input';
import { VisitModifierPayloadInput } from './dto/post/visit-modifier-payload-input';
import { CalendarVisitEvent, calendarVisitEventProps } from './dto/visit';
import { CREATE_VISIT, UPDATE_VISIT } from './mutations';
import { GET_VISIT_EVENTS, GET_VISIT_EVENTS_FOR_PLANNING } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'planningService',
  endpoint: 'planning',
};

@Injectable({
  providedIn: 'root',
})
export class CamVisitEventsService extends CamBaseService {
  protected _graphEndpoint = graphEndpoint;

  readonly visitEvents = new HandleComplexRequest<CalendarVisitEvent[]>();
  readonly visitEvent = new HandleComplexRequest<CalendarVisitEvent>();
  readonly visitsByMission = new HandleComplexRequest<CalendarVisitEvent[]>();
  readonly visitsByMissions = new HandleComplexRequest<CalendarVisitEvent[]>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchById$(id: string) {
    return this.visitEvent.fetch(
      id,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarVisitEvent>>(
          GET_VISIT_EVENTS_FOR_PLANNING({
            where: {
              id: { eq: id },
            },
            props: `
              items {
                ${calendarVisitEventProps.get('id')}
                ${calendarVisitEventProps.get('start')}
                ${calendarVisitEventProps.get('end')}
                ${calendarVisitEventProps.get('isAllDay')}
                ${calendarVisitEventProps.get('status')}
                ${calendarVisitEventProps.get('title')}
                ${calendarVisitEventProps.get('title')}
                ${calendarVisitEventProps.get('userId')}
                ${calendarVisitEventProps.get('user')} {
                  ${userProps.get('id')}
                  ${userProps.get('firstName')}
                  ${userProps.get('lastName')}
                }
                ${calendarVisitEventProps.get('missionId')}
                ${calendarVisitEventProps.get('mission')} {
                  ${missionProps.get('id')}
                  ${missionProps.get('title')}
                  ${missionProps.get('type')}
                  ${missionProps.get('projectId')}
                  ${missionProps.get('project')} {
                    ${projectProps.get('id')}
                    ${projectProps.get('name')}
                    ${projectProps.get('internalReference')}
                    ${projectProps.get('projectAddress')} {
                      ${projectAddressProps.get('id')}
                      ${projectAddressProps.get('city')}
                      ${projectAddressProps.get('country')}
                      ${projectAddressProps.get('postCode')}
                      ${projectAddressProps.get('street')}
                    }
                    ${projectProps.get('contact')} {
                      ${contactProps.get('id')}
                      ${contactProps.get('firstName')}
                      ${contactProps.get('lastName')}
                    }
                  }
                }
              },
              totalCount
            `,
          }),
          graphAggregatorEndpoint.clientName
        )
        .pipe(
          map(data => (data.items && data.items.length === 1 ? data.items[0] : null)),
          filter(isNonNullable)
        )
    );
  }

  public fetchForMissions$(missionIds: string[]) {
    return this.visitsByMissions.fetch(
      missionIds.join('-'),
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarVisitEvent>>(
          GET_VISIT_EVENTS<CalendarVisitEvent>({
            where: { missionId: { in: missionIds } },
            props: `
              items {
                ${calendarVisitEventProps.get('id')}
                ${calendarVisitEventProps.get('start')}
                ${calendarVisitEventProps.get('end')}
                ${calendarVisitEventProps.get('isAllDay')}
                ${calendarVisitEventProps.get('title')}
                ${calendarVisitEventProps.get('missionId')}
                ${calendarVisitEventProps.get('status')}
                ${calendarVisitEventProps.get('userId')}
              }
            `,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchForUser$(userId: string, startDate: DateLike, endDate: DateLike) {
    return this.visitEvents.fetch(
      userId,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarVisitEvent>>(
          GET_VISIT_EVENTS_FOR_PLANNING({
            where: {
              userId: { eq: userId },
              start: { gte: toServerDate(startDate), lte: toServerDate(endDate) },
            },
            props: `
              items {
                ${calendarVisitEventProps.get('id')}
                ${calendarVisitEventProps.get('start')}
                ${calendarVisitEventProps.get('end')}
                ${calendarVisitEventProps.get('isAllDay')}
                ${calendarVisitEventProps.get('status')}
                ${calendarVisitEventProps.get('title')}
                ${calendarVisitEventProps.get('title')}
                ${calendarVisitEventProps.get('userId')}
                ${calendarVisitEventProps.get('missionId')}
                ${calendarVisitEventProps.get('mission')} {
                  ${missionProps.get('id')}
                  ${missionProps.get('title')}
                  ${missionProps.get('type')}
                  ${missionProps.get('projectId')}
                  ${missionProps.get('project')} {
                    ${projectProps.get('id')}
                    ${projectProps.get('name')}
                    ${projectProps.get('internalReference')}
                    ${projectProps.get('projectAddress')} {
                      ${projectAddressProps.get('id')}
                      ${projectAddressProps.get('city')}
                      ${projectAddressProps.get('country')}
                      ${projectAddressProps.get('postCode')}
                      ${projectAddressProps.get('street')}
                    }
                    ${projectProps.get('contact')} {
                      ${contactProps.get('id')}
                      ${contactProps.get('firstName')}
                      ${contactProps.get('lastName')}
                    }
                  }
                }
              },
              totalCount
            `,
          }),
          graphAggregatorEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchForUsers$(usersId: string[], startDate: Date, endDate: Date) {
    return this.visitEvents.fetch(
      usersId.join('-'),
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarVisitEvent>>(
          GET_VISIT_EVENTS<CalendarVisitEvent>({
            where: {
              userId: { in: usersId },
              start: { gte: startDate.toISOString(), lte: endDate.toISOString() },
            },
            props: `
              items {
                ${calendarVisitEventProps.get('id')}
                ${calendarVisitEventProps.get('start')}
                ${calendarVisitEventProps.get('end')}
                ${calendarVisitEventProps.get('isAllDay')}
                ${calendarVisitEventProps.get('title')}
                ${calendarVisitEventProps.get('userId')}
              }
            `,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchForMission$(missionId: string) {
    return this.visitsByMission.fetch(
      missionId,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarVisitEvent>>(
          GET_VISIT_EVENTS<CalendarVisitEvent>({
            where: { missionId: { eq: missionId } },
            props: `
              items {
                ${calendarVisitEventProps.get('id')}
                ${calendarVisitEventProps.get('start')}
                ${calendarVisitEventProps.get('end')}
                ${calendarVisitEventProps.get('isAllDay')}
                ${calendarVisitEventProps.get('title')}
                ${calendarVisitEventProps.get('missionId')}
                ${calendarVisitEventProps.get('status')}
                ${calendarVisitEventProps.get('userId')}
              }
            `,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public createVisit$(visits: VisitCreationPayloadInput[]) {
    return combineLatest(
      visits.map(visit =>
        this._graphService
          .mutateBuilder<CalendarEntry>(CREATE_VISIT(visit), graphEndpoint.clientName, ['visits'])
          .pipe(filter(isNonNullable))
      )
    );
  }

  public update$(visit: Partial<VisitModifierPayloadInput>) {
    return this._graphService.mutateBuilder<CalendarEntry>(
      UPDATE_VISIT({ ...visit, ...graphQlUpdateFields(visit) }),
      graphEndpoint.clientName,
      ['visits']
    );
  }
}
